import { Fragment, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { api, imHintergrund, Rechenabbruch } from './api'
import {
  abonniereRechenlaeufe,
  istEigen,
  oeffneSeite,
  rechenlaeufe,
  stosseAn as stosseImHintergrundAn,
  vergiss,
} from './Rechenlaeufe'
import { Abbruchweg } from './Abbruchverweise'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { ohneStatistikkonten } from './Festkonten'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import type { Auswahleintrag } from './Auswahlfeld'
import { Nachfrage } from './Nachfrage'
import { Periodenfeld } from './Periodenfeld'
import { Prozessfenster } from './Prozessfenster'
import { useDarf } from './Rechte'
import { bezugVon, Schrittfolge } from './Schrittfolge'
import {
  anwendername,
  anzahlwort,
  beschriftung,
  katalogbezeichnung,
  kreisname,
  periodenname,
  satz,
  t,
  zeitpunkt,
} from './sprache'
import type { Sprachschluessel } from './sprache'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import type {
  Abschlusszeile,
  Handbuchausgabe,
  Laufbericht,
  Listeneintrag,
  Schrittbild,
  Schrittzeile,
  Rechenstand,
  Stammdatenkatalog,
} from './typen'

/** Ersatz, wenn eine Auswahlliste im Hintergrund nicht kam (R1212): keine Wahl. */
const KEINE_KONTEN: Auswahleintrag[] = []
/** Solange das Konzernhandbuch nicht da ist, gilt keine Ausgabe — gesagt wird es erst danach (R1210). */
const KEINE_AUSGABEN: Handbuchausgabe[] = []

/**
 * Die Konzernabschlüsse — anlegen und anstoßen (2.13 Abschnitt 8, R787–R789).
 *
 * **Die kausale Kette des Auftraggebers**, hier als Bildschirm: *„1. Ein
 * Konsolidierungskreis mit Stammdatenregister. 2. Ein Konzernabschlußlauf mit
 * Bezug auf einen existierenden Kreis … 3. Ein Auslöser, also Knopf, den wir
 * drücken können."*
 *
 * **Eine Zeile sagt, wie weit er ist**: wie viele Prozesse unter ihm stecken,
 * wie viele seiner zwölf Monate gerechnet und wie viele freigegeben sind.
 * Freigegeben wird je Monat, nicht je Klammer (R787) — deshalb stehen beide
 * Zahlen nebeneinander und nicht ein Zustandswort.
 *
 * **Die Vorlage ist die Abfederung** (R789): Ein neuer Konzernabschluß schöpft
 * die Prozesse eines bestehenden, statt daß jemand sie zwölfmal neu wählt.
 */
/**
 * Die Handbuchausgabe, die für diesen Konzernabschluß gilt (R779, R817).
 *
 * **Gesucht wird nach dem ersten Monat des Geschäftsjahres**: Eine Ausgabe,
 * die mitten im Jahr in Kraft tritt, gilt für den Abschluß erst im nächsten —
 * und die Werte, die das Fenster zeigt, sollen die sein, mit denen gerechnet
 * wurde.
 */
function geltendeAusgabe(
  ausgaben: Handbuchausgabe[],
  abschluesse: Abschlusszeile[],
  schluessel: string,
): Handbuchausgabe | null {
  const abschluss = abschluesse.find((a) => a.schluessel === schluessel)

  if (!abschluss) {
    return null
  }

  return (
    ausgaben.find(
      (x) =>
        x.gueltigVon <= abschluss.vonPeriode
        && (x.gueltigBis === null || x.gueltigBis >= abschluss.vonPeriode),
    ) ?? null
  )
}
/**
 * Das Zeichen für einen Zustand (R823, R143).
 *
 * **Die Ampel gibt es im Haus schon** — sie steht im Systemzustand und an der
 * Stammdatenpflege. Sie bekommt hier keine zweite Gestalt, nur eine dritte
 * Ausprägung: *nicht ausgeführt* ist grau und nicht farblos.
 *
 * **Und sie trägt ihr Wort** (R143): Die Farbe allein wäre für jeden zehnten
 * Mann keine Auskunft, und im Ausdruck ist sie gar keine. Was sie sagt, steht
 * als Beschriftung an ihr — samt der Zahl, die bis zum 2026-09-27 als eigene
 * Spalte danebenstand.
 */
function Zustandszeichen({ zustand, sagt }: { zustand: string; sagt: string }) {
  const ton = zustand === 'fertig' ? 'gut' : zustand === 'unvollstaendig' ? 'schlecht' : 'fehlt'

  return <span className={`ampel ${ton}`} title={sagt} aria-label={sagt} role="img" />
}

/**
 * **Der Balken an der Zeile, solange gerechnet wird** (7.2 Abschnitt 40,
 * R1252): Er wächst mit jedem fertigen Monat, nach dem zwölften heißt es
 * „Speichert …". Hat ein anderer angestoßen, steht dabei, wer und wann
 * (R1254) — beim eigenen Lauf wäre das keine Auskunft.
 */
function Rechenbalken({ stand }: { stand: Rechenstand }) {
  const anteil = stand.monate > 0 ? Math.round((stand.fertig / stand.monate) * 100) : 0

  return (
    <div className="rechenbalken">
      <div
        className="balken"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={stand.monate}
        aria-valuenow={stand.fertig}
        aria-label={t('konzernabschluss.rechnen.titel')}
      >
        <div style={{ width: `${anteil}%` }} />
      </div>
      <span role="status">
        {stand.zustand === 'speichert'
          ? t('konzernabschluss.speichert')
          : // Genannt wird der Monat, an dem gerechnet wird; der Balken zeigt die fertigen.
            t('konzernabschluss.rechnet', {
              fertig: String(Math.min(stand.fertig + 1, stand.monate)),
              monate: String(stand.monate),
            })}
      </span>
      {!istEigen(stand.konzernabschluss) && (
        <span className="klein gedaempft">
          {t('konzernabschluss.rechnet.wer', { wann: zeitpunkt(stand.seit), wer: anwendername(stand.von) })}
        </span>
      )}
    </div>
  )
}

export function Konzernabschluesse() {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('konzernabschluesse:konzernabschluesse')
  // Was der Angemeldete nicht darf, ist nicht da (8.1 Abschnitt 7, R1180).
  const darf = useDarf()

  const [kreise, setKreise] = useState<Listeneintrag[]>([])
  const [ledger, setLedger] = useState<Listeneintrag[]>([])
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [bericht, setBericht] = useState<Laufbericht | null>(null)
  const [anlegen, setAnlegen] = useState(false)
  const [anstossen, setAnstossen] = useState<Abschlusszeile | null>(null)
  /**
   * Welcher Konzernabschluß seine Prozesse zeigt — einer zur Zeit (R610, R814).
   *
   * **Zwei offene Folgen nebeneinander wären zwei Ordnungen auf einem
   * Bildschirm**, und die Pfeile daneben gehörten dann sichtbar zu keiner von
   * beiden.
   */
  const [aufgeklappt, setAufgeklappt] = useState<string | null>(null)
  const [folge, setFolge] = useState<Schrittbild | null>(null)
  /** Der Prozeß, dessen Fenster offensteht (R814). */
  const [fenster, setFenster] = useState<Schrittzeile | null>(null)
  const [entfernen, setEntfernen] = useState<Schrittzeile | null>(null)
  /** Welches Werkzeug als nächster Prozeß dazukommt (R814). */
  const [neuesWerkzeug, setNeuesWerkzeug] = useState('')
  const [entwurf, setEntwurf] = useState<{
    name: string
    kreis: string
    ledger: string
    von: number | null
    vorlage: string
  }>({ name: '', kreis: '', ledger: '', von: null, vorlage: '' })

  // Die Abschlüsse sind der Bestand der Seite — ein Hauptabruf (R1211). Kamen
  // sie nicht, steht da „Ließ sich nicht laden" und nicht „kein Abschluss".
  const abschluesseabruf = useAbruf(() => api.konzernabschluesse(), [])
  const abschluesse = abschluesseabruf.wert

  /*
    **Der Weg aus dem Bericht** (2.13 Abschnitt 14, R1112): Ohne Konzernabschluss
    gibt es keine Konzernzahlen, und die Kopfzeile über dem Bericht führt
    hierher — `?rechnen=KAB-2` öffnet das Anstoßen, `?anlegen=KREIS&ledger=…&von=…`
    das Anlegen mit Kreis, Bewertungsebene und Geschäftsjahresbeginn.
  */
  const vorgabe = useMemo(() => new URLSearchParams(window.location.hash.split('?')[1] ?? ''), [])

  useEffect(() => {
    const kreis = vorgabe.get('anlegen')
    if (kreis) {
      setAnlegen(true)
      setEntwurf((v) => ({
        ...v,
        kreis,
        ledger: vorgabe.get('ledger') ?? '',
        von: Number(vorgabe.get('von')) || null,
      }))
    }
  }, [vorgabe])

  /*
    **Die Vorgabe gilt einmal** (Befund vom 2026-10-06): Nach dem Rechnen lädt
    die Seite ihre Liste neu, und bis dahin stand `?rechnen=` noch in der
    Adresse — die Rückfrage kam nach dem erfolgreichen Ende ein zweites Mal.
    Eingelöst wird sie deshalb einmal und dann aus der Adresse gestrichen;
    auch ein Neuladen öffnet sie nicht wieder.
  */
  const rechnenEingeloest = useRef(false)
  useEffect(() => {
    const schluessel = vorgabe.get('rechnen')
    const gefunden = schluessel ? abschluesse?.find((a) => a.schluessel === schluessel) : undefined
    if (gefunden && !rechnenEingeloest.current) {
      rechnenEingeloest.current = true
      setAnstossen(gefunden)
      const [pfad, frage] = window.location.hash.split('?')
      const rest = new URLSearchParams(frage ?? '')
      rest.delete('rechnen')
      // Ohne neuen Eintrag im Verlauf und ohne Seitenwechsel.
      const uebrig = rest.toString()
      window.history.replaceState(window.history.state, '', uebrig ? `${pfad}?${uebrig}` : pfad)
    }
  }, [vorgabe, abschluesse])

  useEffect(() => {
    let gilt = true
    // Nachschlagelisten, im Hintergrund (7.2 Abschnitt 30, R1007).
    imHintergrund(() => api.stammdatenliste('konsolidierungskreis'))
      .then((l) => gilt && setKreise(l.eintraege))
      .catch(() => {})
    imHintergrund(() => api.stammdatenliste('ledger'))
      .then((l) => gilt && setLedger(l.eintraege))
      .catch(() => {})

    return () => {
      gilt = false
    }
  }, [])

  /*
    **Das Konzernhandbuch und der Kontenrahmen kommen einmal**, nicht bei jedem
    geöffneten Prozeß: Das Fenster zeigt beide, und zwölf Öffnungen wären sonst
    zwölf Rufe für dieselbe Auskunft.
  */
  // **Das Handbuch ist ein Hauptabruf** (7.2 Abschnitt 39, R1211): An ihm
  // hängt im Prozessfenster der Satz, ob eine Ausgabe gilt. Bis zum
  // 2026-10-06 lief es still, und ein Ausfall hieß dort „Für diesen
  // Zeitraum gilt keine Ausgabe des Konzernhandbuchs."
  const handbuchabruf = useAbruf(() => api.konzernhandbuch(), [])
  const handbuch = handbuchabruf.wert ?? KEINE_AUSGABEN

  // Namen und Wahlvorrat des Fensters, im Hintergrund (R1007, R1211).
  const katalog = useHintergrundabruf<Stammdatenkatalog | null>(() => api.stammdatenkatalog(), [], null)
  // Ohne Statistikkonten: Die Konsolidierung bebucht sie nie (R1734).
  const konten = useHintergrundabruf(
    () => api.stammdatenliste('konto').then((l) => ohneStatistikkonten(l.eintraege, (e) => e.schluessel).map(ausStammdatum)),
    [],
    KEINE_KONTEN,
  )

  const positionswahl = useMemo(
    () =>
      (katalog?.positionen ?? []).map((p) => ({
        wert: p.schluessel,
        text: katalogbezeichnung(p),
        stillgelegtAb: p.stillgelegtAb,
      })),
    [katalog],
  )

  /**
   * Klappt einen Konzernabschluß auf — und holt dabei seine Prozesse (R814).
   *
   * **Geholt wird beim Aufklappen und nicht auf Vorrat**: Sechs
   * Konzernabschlüsse wären sechs Rufe für Folgen, die niemand ansieht.
   */
  async function klappeAuf(a: Abschlusszeile) {
    if (aufgeklappt === a.schluessel) {
      setAufgeklappt(null)
      return
    }

    setAufgeklappt(a.schluessel)
    setFolge(null)
    setMeldung(null)

    try {
      setFolge(await api.konsolidierungsschritte(a.schluessel))
    } catch (f) {
      // Nur ein fachlicher Satz steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(f))
    }
  }

  /**
   * Führt einen Handgriff an der Folge aus und holt sie frisch.
   *
   * **Geholt wird die Folge, die dasteht** — und nicht die Werksvorlage: Wer
   * einen Prozeß seines Konzernabschlusses verschiebt, sähe sonst nach jedem
   * Handgriff eine fremde Ordnung.
   */
  async function tueAnDerFolge(was: () => Promise<Schrittzeile[]>) {
    if (aufgeklappt === null) {
      return
    }

    setLaeuft(true)
    setMeldung(null)

    try {
      await was()
      setFolge(await api.konsolidierungsschritte(aufgeklappt))
      abschluesseabruf.setze(await api.konzernabschluesse())
      setEntfernen(null)
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  const kreiswahl = useMemo(() => kreise.map(ausStammdatum), [kreise])
  const ledgerwahl = useMemo(() => ledger.map(ausStammdatum), [ledger])

  /**
   * Die Vorlagen: alle bestehenden Abschlüsse — und die Fassung ab Werk.
   *
   * **Sie steht als erster Eintrag und nicht als leere Wahl**: „nichts
   * gewählt" hieße sonst „ohne Prozesse", und das ist etwas anderes.
   */
  const vorlagenwahl = useMemo(
    () => [
      { wert: '', text: t('konzernabschluss.vorlage.werk') },
      ...(abschluesse ?? []).map((a) => ({
        wert: a.schluessel,
        text: beschriftung(a.namen) || a.schluessel,
      })),
    ],
    [abschluesse],
  )

  async function tue(was: () => Promise<Abschlusszeile[]>, hinweis?: string) {
    setLaeuft(true)
    setMeldung(null)

    try {
      abschluesseabruf.setze(await was())
      setAnlegen(false)
      // Was gelang, sagt das Kästchen oben rechts (7.2 Abschnitt 34, R1130).
      if (hinweis) {
        meldeErfolg(hinweis)
      }
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  /*
    **Gerechnet wird im Hintergrund** (7.2 Abschnitt 40, R1252): Der Anstoß
    kommt sofort zurück, und an der Zeile steht der Balken. Das Ende meldet die
    gemeinsame Stelle (`Rechenlaeufe.ts`) — das Kästchen, wo der Anwender
    gerade ist (R1253); was hier stehen bleiben muss, kommt als Ergebnis.
  */
  async function stosseAn(a: Abschlusszeile) {
    setMeldung(null)
    setBericht(null)
    setAnstossen(null)

    try {
      await stosseImHintergrundAn(a.schluessel, beschriftung(a.namen) || a.schluessel)
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    }
  }

  const laeufe = useSyncExternalStore(abonniereRechenlaeufe, rechenlaeufe)

  // Solange die Seite offen ist, sieht sie auch die Läufe anderer (R1254).
  useEffect(() => oeffneSeite(), [])

  /*
    **Der Bericht bleibt nur stehen, wenn er etwas zu sagen hat** (R790,
    R1131): einen Fehler oder Befunde. Gespeichert sagt das Kästchen. Ein
    fachlicher Abbruch steht am Ort, mit dem Satz des Dienstes (R1004).
  */
  useEffect(() => {
    const ergebnis = laeufe.ergebnisse[0]
    if (!ergebnis) {
      return
    }
    vergiss(ergebnis)
    const { abbruch, bericht: lauf } = ergebnis.stand
    if (abbruch?.meldung) {
      setMeldung(fachlicherSatz(new Rechenabbruch(abbruch.meldung, abbruch.handreichung)))
    } else if (lauf) {
      setBericht(lauf)
    }
  }, [laeufe.ergebnisse])

  /* Ist ein Lauf zu Ende — auch der eines anderen —, zeigt die Zeile den neuen Stand. */
  const laufend = Object.values(laeufe.staende)
    .filter((s) => s.laeuft)
    .map((s) => s.konzernabschluss)
    .join(' ')
  const vorher = useRef(laufend)
  useEffect(() => {
    const beendet = vorher.current.split(' ').some((k) => k !== '' && !laufend.split(' ').includes(k))
    vorher.current = laufend
    if (beendet) {
      void api.konzernabschluesse().then(abschluesseabruf.setze, () => {})
    }
  }, [laufend, abschluesseabruf.setze])

  if (abschluesse === null) {
    return (
      <section className="konzernabschluesse">
        <h1>{t('bereich.abschluss.konzernabschluesse')}</h1>
        {abschluesseabruf.ausgefallen && <Abrufausfall abruf={abschluesseabruf} />}
      </section>
    )
  }

  // **Die Zelle trägt den ausgeschriebenen Monat** (7.2 Abschnitt 21, R839 —
  // Entscheidung des Auftraggebers vom 2026-09-30): Die Kurzform „Dez. 2026"
  // gehört dem engen Spaltenkopf, und nur ihm. Hier stand sie in der Spalte
  // Geschäftsjahr: „Jan. 2026 – Dez. 2026".
  const monate = (a: Abschlusszeile) =>
    `${periodenname(a.vonPeriode)} – ${periodenname(a.bisPeriode)}`

  return (
    <section className="konzernabschluesse">
      <h1>{t('bereich.abschluss.konzernabschluesse')}</h1>

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('konzernabschluss.spalte.name')}</th>
              <th>{t('konzernabschluss.spalte.kreis')}</th>
              <th>{t('konzernabschluss.spalte.geschaeftsjahr')}</th>
              {/*
                **Zwei Zahlen sind ein Zeichen geworden** (Auftrag vom
                2026-09-27): „Prozesse" und „12 von 12" beantworteten eine
                Frage, die man beim Aufklappen ohnehin sieht. Statt dessen
                steht da, wann zuletzt gerechnet wurde und von wem -- und
                davor das Zeichen, das den Fehler sofort zeigt.
              */}
              <th className="zustandsspalte">{t('konzernabschluss.spalte.zustand')}</th>
              <th>{t('konzernabschluss.spalte.aktualisiert')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {abschluesse.length === 0 && (
              <tr>
                <td colSpan={6} className="leerhinweis">
                  {t('konzernabschluss.leer')}
                </td>
              </tr>
            )}
            {abschluesse.map((a) => {
              const offen = aufgeklappt === a.schluessel
              const rechnet = laeufe.staende[a.schluessel]?.laeuft ? laeufe.staende[a.schluessel] : undefined
              return (
                <Fragment key={a.schluessel}>
                <tr className={offen ? 'aufgeklappt' : undefined}>
                  {/*
                    **Die Prozesse stehen unter ihrem Konzernabschluß** (R814):
                    *„Für den Anwender bedeutet das ein Springen in der
                    Struktur weniger."* Die Zahl daneben sagt, wie viele es
                    sind — welche es sind, sagt erst das Aufklappen.
                  */}
                  <td>
                    <button
                      type="button"
                      className="aufklapper"
                      aria-expanded={offen}
                      title={t('prozess.oeffnen')}
                      onClick={() => void klappeAuf(a)}
                    >
                      {offen ? '▾' : '▸'}
                    </button>
                    {beschriftung(a.namen) || a.schluessel}
                  </td>
                  <td className="gedaempft">
                    {/* Über den Namensgeber, nicht aus der eigenen Liste: Bis die da
                        war, stand „KRS-000016-18“ (R1660, Wächter 2026-10-12). */}
                    {kreisname(a.kreis)}
                  </td>
                  <td>{monate(a)}</td>
                  {/*
                    **Das Zeichen trägt die Zahl als Beschriftung** (R143,
                    R823): „Neun von zwölf Monaten gerechnet" steht an ihm und
                    nicht mehr als eigene Spalte. Die Freigabe steht dabei,
                    wo es eine gibt — sie gilt je Monat und nicht für die
                    Klammer (R787).
                  */}
                  <td className="zustandsspalte">
                    <Zustandszeichen
                      zustand={a.zustand}
                      sagt={
                        a.zustand === 'offen'
                          ? t('konzernabschluss.zustand.offen')
                          : t('konzernabschluss.zustand.gerechnet', { anzahl: anzahlwort(a.gerechnet) })
                      }
                    />
                  </td>
                  <td className="gedaempft">
                    {a.aktualisiertAm === null ? (
                      t('konzernabschluss.nichtgerechnet')
                    ) : (
                      <>
                        {zeitpunkt(a.aktualisiertAm)}
                        {a.aktualisiertVon && (
                          <span className="klein gedaempft">
                            {' · '}
                            {t('konzernabschluss.von', { wer: anwendername(a.aktualisiertVon) })}
                          </span>
                        )}
                        {a.freigegeben > 0 && (
                          <span className="klein gedaempft">
                            {' · '}
                            {t('konzernabschluss.freigegeben', { anzahl: anzahlwort(a.freigegeben) })}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="handgriff">
                    {rechnet ? (
                      <Rechenbalken stand={rechnet} />
                    ) : darf('konzernabschlussAnstossen') && (
                      <button
                        type="button"
                        className="schmal"
                        disabled={laeuft}
                        onClick={() => setAnstossen(a)}
                      >
                        {t('konzernabschluss.rechnen')}
                      </button>
                    )}
                  </td>
                </tr>

                {offen && folge === null && (
                  <tr className="prozesszeile">
                    <td colSpan={6} className="leerhinweis">
                      {t('konzernabschluss.prozesse.laedt')}
                    </td>
                  </tr>
                )}

                {offen
                  && (
                    <Schrittfolge
                      bauart="abschluss"
                      folge={folge?.folge ?? []}
                      werkzeuge={folge?.werkzeuge ?? []}
                      laeuft={laeuft}
                      aufOeffnen={setFenster}
                      aufEntfernen={darf('konsolidierungsschrittEntfernen') ? setEntfernen : undefined}
                    />
                  )}

                {/*
                  **Anlegen gehört an dieselbe Stelle wie Ordnen und
                  Entfernen** (R814): Wer sieht, daß ein Prozeß fehlt, soll ihn
                  dort anlegen, wo er ihn vermißt — und nicht auf einem anderen
                  Bildschirm. Den Namen des Werkzeugs bekommt er als Vorschlag;
                  umbenannt wird er, wo er steht.
                */}
                {offen && folge !== null && darf('konsolidierungsschrittPflegen') && (
                  <tr className="prozesszeile anlegen">
                    <td />
                    <td colSpan={4}>
                      <Auswahlfeld
                        wert={neuesWerkzeug}
                        eintraege={(folge.werkzeuge ?? []).map((w) => ({
                          wert: w.schluessel,
                          text: t(`werkzeug.${w.schluessel}` as Sprachschluessel),
                        }))}
                        sorte="aufzaehlung"
                        leer={t('schritte.feld.werkzeug.leer')}
                        beschriftung={t('schritte.feld.werkzeug')}
                        abgeschaltet={laeuft}
                        aufWahl={setNeuesWerkzeug}
                      />
                    </td>
                    <td className="handgriff">
                      <button
                        type="button"
                        className="schmal"
                        disabled={laeuft || neuesWerkzeug === ''}
                        onClick={() =>
                          void tueAnDerFolge(() =>
                            api.konsolidierungsschrittPflegen(
                              null,
                              neuesWerkzeug,
                              t(`werkzeug.${neuesWerkzeug}` as Sprachschluessel),
                              undefined,
                              aufgeklappt,
                            ),
                          ).then(() => setNeuesWerkzeug(''))
                        }
                      >
                        {t('schritte.neu')}
                      </button>
                    </td>
                  </tr>
                )}

                {offen && folge?.folge.length === 0 && (
                  <tr className="prozesszeile">
                    <td colSpan={6} className="leerhinweis">
                      {t('schritte.leer')}
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {!anlegen && darf('konzernabschlussAnlegen') && (
        <div className="knopfreihe">
          <button type="button" disabled={laeuft} onClick={() => setAnlegen(true)}>
            {t('konzernabschluss.neu')}
          </button>
        </div>
      )}

      {anlegen && darf('konzernabschlussAnlegen') && (
        <div className="stammdatenfelder abschlussanlage">
          <label>
            <span className="name">{t('konzernabschluss.feld.name')}</span>
            <input
              value={entwurf.name}
              onChange={(e) => setEntwurf((v) => ({ ...v, name: e.target.value }))}
            />
            <span className="erlaeuterung">{t('konzernabschluss.feld.name.hinweis')}</span>
          </label>

          <label>
            <span className="name">{t('konzernabschluss.feld.kreis')}</span>
            <Auswahlfeld
              wert={entwurf.kreis}
              eintraege={kreiswahl}
              leer={t('konzernabschluss.feld.kreis.leer')}
              beschriftung={t('konzernabschluss.feld.kreis')}
              aufWahl={(kreis) => setEntwurf((v) => ({ ...v, kreis }))}
            />
          </label>

          <label>
            <span className="name">{t('konzernabschluss.feld.ledger')}</span>
            <Auswahlfeld
              wert={entwurf.ledger}
              eintraege={ledgerwahl}
              leer={t('konzernabschluss.feld.ledger.leer')}
              beschriftung={t('konzernabschluss.feld.ledger')}
              aufWahl={(l) => setEntwurf((v) => ({ ...v, ledger: l }))}
            />
            <span className="erlaeuterung">{t('konzernabschluss.feld.ledger.hinweis')}</span>
          </label>

          <Periodenfeld
            klasse=""
            wert={entwurf.von}
            beschriftung={t('konzernabschluss.feld.von')}
            erlaeuterung={t('konzernabschluss.feld.von.hinweis')}
            aufWert={(von) => setEntwurf((v) => ({ ...v, von }))}
          />

          <label>
            <span className="name">{t('konzernabschluss.feld.vorlage')}</span>
            <Auswahlfeld
              wert={entwurf.vorlage}
              eintraege={vorlagenwahl}
              sorte="aufzaehlung"
              beschriftung={t('konzernabschluss.feld.vorlage')}
              aufWahl={(vorlage) => setEntwurf((v) => ({ ...v, vorlage }))}
            />
            <span className="erlaeuterung">{t('konzernabschluss.feld.vorlage.hinweis')}</span>
          </label>

          <div className="aktionsreihe">
            <button
              type="button"
              className="haupt"
              disabled={
                laeuft
                || entwurf.name.trim() === ''
                || entwurf.kreis === ''
                || entwurf.ledger === ''
                || entwurf.von === null
              }
              onClick={() =>
                void tue(
                  () =>
                    api.konzernabschlussAnlegen(
                      entwurf.name,
                      entwurf.kreis,
                      entwurf.ledger,
                      entwurf.von!,
                      entwurf.vorlage || null,
                    ),
                  t('erfolg.konzernabschluss.angelegt'),
                ).then(() => setEntwurf({ name: '', kreis: '', ledger: '', von: null, vorlage: '' }))
              }
            >
              {t('konzernabschluss.anlegen')}
            </button>
            <button type="button" disabled={laeuft} onClick={() => setAnlegen(false)}>
              {t('konzernabschluss.abbrechen')}
            </button>
          </div>
        </div>
      )}

      {meldung && <p className="meldung">{meldung}</p>}

      {/*
        **Der Bericht des Laufs bleibt stehen, bis jemand ihn wegklickt.** Ein
        Hinweis, der nach drei Sekunden verschwindet, ist bei zwölf Monaten
        keine Auskunft — und die Befunde darin sind das Wichtigste, was der
        Lauf zu sagen hat.
      */}
      {bericht && (
        <div className="laufbericht">
          {/*
            **Gespeichert ist nicht dasselbe wie gerechnet** (R790): Gerechnet
            wird immer das ganze Geschäftsjahr — auch wenn der erste Monat
            scheitert, denn sonst sähe der Anwender nur den ersten Fehler und
            nach dessen Behebung den zweiten. Geschrieben wird nur, wenn kein
            einziger Monat einen Fehler trägt.
          */}
          {bericht.gespeichert === 0 && (
            <p className="meldung fehler">
              {/* „1 Monat trägt“, nicht „1 Monate tragen“ (Befund vom 2026-10-11). */}
              {bericht.befunde.filter((b) => b.schwere === 'fehler').length === 1
                ? t('konzernabschluss.verweigert.eins')
                : t('konzernabschluss.verweigert', {
                    anzahl: anzahlwort(bericht.befunde.filter((b) => b.schwere === 'fehler').length),
                  })}
            </p>
          )}

          {bericht.befunde.length > 0 && (
            <ul className="befunde">
              {bericht.befunde.map((b) => (
                <li key={b.periode} className={b.schwere === 'fehler' ? 'fehlt' : 'abweichend'}>
                  <strong>{periodenname(b.periode)}</strong>
                  {': '}
                  {satz(b.meldung)}
                  {/*
                    **Der Weg steht unter der Meldung, mit Verweis an den Ort**
                    (7.1 Abschnitt 13, R1581) — Kreis und Bewertungsebene aus
                    dem Konzernabschluss, der gerechnet wurde.
                  */}
                  {b.handreichung && (
                    <p className="handreichung">
                      <Abbruchweg
                        handreichung={b.handreichung}
                        kontext={abschluesse?.find((a) => a.schluessel === bericht.konzernabschluss) ?? {}}
                      />
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="knopfreihe">
            <button type="button" onClick={() => setBericht(null)}>
              {t('konzernabschluss.laufhin.schliessen')}
            </button>
          </div>
        </div>
      )}

      {/*
        **Das Fenster gehört zum Prozeß, nicht zur Seite** (R814): Es geht
        über der Folge auf, und die Folge bleibt dahinter stehen — wer es
        schließt, steht wieder da, wo er war.
      */}
      {fenster && aufgeklappt && (
        <Prozessfenster
          schritt={fenster}
          werkzeug={folge?.werkzeuge.find((w) => w.schluessel === fenster.werkzeug)}
          konzernabschluss={aufgeklappt}
          parameter={geltendeAusgabe(handbuch, abschluesse, aufgeklappt)?.parameter ?? null}
          ausgabe={geltendeAusgabe(handbuch, abschluesse, aufgeklappt)?.ausgabe ?? null}
          handbuchabruf={handbuchabruf}
          kontowahl={konten}
          positionswahl={positionswahl}
          bezug={bezugVon(abschluesse, aufgeklappt)}
          aufGespeichert={(neu) =>
            setFolge((bisher) => (bisher === null ? bisher : { ...bisher, folge: neu }))
          }
          aufSchliessen={() => setFenster(null)}
        />
      )}

      {entfernen && darf('konsolidierungsschrittEntfernen') && (
        <Nachfrage
          titel={t('schritte.entfernen.titel')}
          frage={t('schritte.entfernen.frage', {
            name: beschriftung(entfernen.namen) || entfernen.schluessel,
          })}
          hinweis={t('schritte.entfernen.hinweis')}
          bestaetigen={t('schritte.entfernen')}
          gefahr
          aufJa={() =>
            void tueAnDerFolge(() =>
              api.konsolidierungsschrittEntfernen(entfernen.schluessel, aufgeklappt),
            )
          }
          aufSchliessen={() => setEntfernen(null)}
        />
      )}

      {anstossen && darf('konzernabschlussAnstossen') && (
        <Nachfrage
          titel={t('konzernabschluss.rechnen.titel')}
          frage={t('konzernabschluss.rechnen.frage', {
            name: beschriftung(anstossen.namen) || anstossen.schluessel,
          })}
          hinweis={t('konzernabschluss.rechnen.hinweis')}
          bestaetigen={t('konzernabschluss.rechnen')}
          aufJa={() => void stosseAn(anstossen)}
          aufSchliessen={() => setAnstossen(null)}
        />
      )}
    </section>
  )
}
